import { Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Col, Form, Spinner } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { coloursArray, truncateText } from '../../../../../../lib/utils';
import {
  fetchJiraBoards,
  fetchSprintCompletionMetrics,
} from '../../../../../../modules/dashboard';
import BrokenSprintsChart from './BrokenSprintsChart';
import SprintPredictabilityChart from './SprintPredictabilityChart';
import VelocityInstabilityChart from './VelocityInstabilityChart';

const SprintCompletionMetrics = ({
  projectId,
  setShowExport,
  isJiraIntegrated,
  selectedJiraProject,
}) => {
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const jiraProjectId = selectedJiraProject?.linked_project_id;

  useEffect(() => {
    setSelectedBoardId(null);
  }, [jiraProjectId]);

  const {
    data: boardsData,
    isLoading: boardsLoading,
    error: boardsError,
  } = useQuery({
    queryKey: ['jiraBoards', projectId, jiraProjectId],
    queryFn: () => fetchJiraBoards(projectId, jiraProjectId),
    enabled: isJiraIntegrated && !!selectedJiraProject,
    onSuccess: () => setShowExport(true),
    onError: () => setShowExport(true),
  });

  const boards = boardsData?.boards || [];

  useEffect(() => {
    if (boards.length > 0 && selectedBoardId == null) {
      setSelectedBoardId(boards[0].id);
    }
  }, [boards, selectedBoardId]);

  const {
    data: metricsData,
    isLoading: metricsLoading,
    error: metricsError,
  } = useQuery({
    queryKey: ['sprintMetrics', projectId, jiraProjectId, selectedBoardId],
    queryFn: () =>
      fetchSprintCompletionMetrics(projectId, jiraProjectId, selectedBoardId),
    enabled: isJiraIntegrated && !!selectedBoardId,
  });

  const chartData = useMemo(() => {
    const summary = metricsData?.sprint_summary || [];
    return summary.map((sprint, index) => ({
      sprintName: sprint.sprint_name,
      completedStoryPoints: sprint.completed_story_points,
      totalStoryPoints: sprint.total_story_points,
      fill: coloursArray[index % coloursArray.length],
    }));
  }, [metricsData]);

  useEffect(() => {
    if (!isJiraIntegrated || !selectedJiraProject) {
      setShowExport(true);
    }
  }, [isJiraIntegrated, selectedJiraProject, projectId]);

  const handleBoardChange = (e) => {
    setSelectedBoardId(Number(e.target.value));
  };

  const loading = boardsLoading || metricsLoading;
  const error = boardsError || metricsError;
  const hasBoards = boards.length > 0;
  const showCharts = !loading && hasBoards && !error;

  return (
    <>
      {isJiraIntegrated && selectedJiraProject && (
        <>
          <Typography
            variant="h5"
            className="dashboard-heading mr-5 text-nowrap">
            Sprint Execution Metrics
          </Typography>
          <p className="text-muted mb-2 text-center" style={{ fontSize: '12px' }}>
            (Showing results for past 5 sprints)
          </p>
        </>
      )}
      <div className="d-flex justify-content-end align-items-center">
        {hasBoards && boards.length > 1 && (
          <>
            <Form.Label style={{ marginRight: '10px' }}>JIRA Board:</Form.Label>
            <Form.Select
              value={selectedBoardId || ''}
              onChange={handleBoardChange}
              disabled={loading}
              aria-label="Select Board"
              style={{ width: '250px' }}>
              <option value="none" disabled>
                Select a JIRA board
              </option>
              {boards.map((board) => (
                <option key={board.id} value={board.id} title={board.name}>
                  {truncateText(board.name)}
                </option>
              ))}
            </Form.Select>
          </>
        )}
      </div>

      {loading ? (
        <div className="my-4 text-center">
          <Spinner animation="border" />
        </div>
      ) : error ? (
        <Alert variant="warning" className="text-center">
          {error?.message || 'Failed to load data. Please try again.'}
        </Alert>
      ) : (
        showCharts && (
          <>
            <Col md={12}>
              <SprintPredictabilityChart
                data={chartData}
                loading={loading}
                isJiraIntegrated={isJiraIntegrated}
              />
            </Col>
            <Col md={12}>
              <VelocityInstabilityChart
                data={chartData}
                loading={loading}
                isJiraIntegrated={isJiraIntegrated}
              />
            </Col>
            <Col md={12}>
              <BrokenSprintsChart
                data={chartData}
                loading={loading}
                isJiraIntegrated={isJiraIntegrated}
              />
            </Col>
          </>
        )
      )}
    </>
  );
};

export default SprintCompletionMetrics;
